import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { getApiErrorCode, getApiErrorMessage } from "api/apiError";
import { Button, FormField, notify, PasswordChecklist, PasswordInput } from "Components/ui";
import { meetsPasswordPolicy, PASSWORD_POLICY_MESSAGE } from "utils/passwordPolicy";
import type { ProfileData } from "../data";
import Section from "./Section";

const schema = z
  .object({
    currPassword: z.string().min(1, "Enter your current password"),
    newPassword: z.string().refine(meetsPasswordPolicy, PASSWORD_POLICY_MESSAGE),
    confirmPassword: z.string().min(1, "Re-enter the new password"),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ["confirmPassword"],
    message: "The passwords don't match",
  });

type PasswordValues = z.infer<typeof schema>;

const EMPTY: PasswordValues = { currPassword: "", newPassword: "", confirmPassword: "" };

/**
 * Change password. The server checks the current one, enforces the same rules as this form, and
 * signs out every other session; this one carries on with a fresh token.
 */
const PasswordSection: React.FC<{ profile: ProfileData }> = ({ profile }) => {
  const [open, setOpen] = React.useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });
  const newPassword = watch("newPassword");

  const onSubmit = async (values: PasswordValues) => {
    try {
      await profile.changePassword({
        currPassword: values.currPassword,
        newPassword: values.newPassword,
      });
      reset(EMPTY);
      setOpen(false);
      notify.success("Password changed", {
        description: "You were signed out everywhere else.",
      });
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === "AUTH_PASSWORD_MISMATCH") {
        setError("currPassword", { message: "That password is incorrect" });
        return;
      }
      if (code === "PROFILE_PASSWORD_REUSED") {
        setError("newPassword", { message: getApiErrorMessage(err) });
        return;
      }
      notify.error(err, { fallback: "Couldn't change your password" });
    }
  };

  const cancel = () => {
    reset(EMPTY);
    setOpen(false);
  };

  return (
    <Section
      title="Password"
      description="Changing it signs you out on every other device and browser."
      aside={
        !open && (
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Change password
          </Button>
        )
      }
    >
      {open && (
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <FormField label="Current password" error={errors.currPassword?.message}>
            <PasswordInput
              autoComplete="current-password"
              autoFocus
              {...register("currPassword")}
            />
          </FormField>
          <div className="space-y-3">
            <FormField label="New password" error={errors.newPassword?.message}>
              <PasswordInput autoComplete="new-password" {...register("newPassword")} />
            </FormField>
            <PasswordChecklist password={newPassword} />
          </div>
          <FormField label="Confirm new password" error={errors.confirmPassword?.message}>
            <PasswordInput autoComplete="new-password" {...register("confirmPassword")} />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={cancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Change password
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
};

export default PasswordSection;
